import { useEffect, useRef } from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { texteExperience, type Candidat } from './classementRemplacants';
import { initiales } from './textesDemande';

interface Props {
  candidats: Candidat[];
  /** La personne actuellement choisie (marquée « choisi·e »). */
  choisi: number | null;
  filtre: string;
  actif: number;
  onFiltre: (v: string) => void;
  onActif: (i: number) => void;
  onChoisir: (id: number) => void;
  /** Texte du champ de recherche. */
  etiquette: string;
}

/** Liste de choix des remplaçant·e·s : champ de recherche + liste (↑ ↓ Entrée), disponibles d'abord, disponibilité et expérience visibles. */
export function ListeRemplacants({ candidats, choisi, filtre, actif, onFiltre, onActif, onChoisir, etiquette }: Props) {
  const f = filtre.trim().toLowerCase();
  const vis = candidats.filter((c) => !f || c.personne.display_name.toLowerCase().includes(f));
  const dispo = vis.filter((c) => c.dispo), non = vis.filter((c) => !c.dispo);
  const liste = [...dispo, ...non];
  const verifiees = candidats.every((c) => c.verifiee);   // faux : les absences n'ont pas pu être chargées, « disponible » ne veut rien dire
  const refActif = useRef<HTMLLIElement>(null);
  useEffect(() => { refActif.current?.scrollIntoView({ block: 'nearest' }); }, [actif]);

  const option = (c: Candidat, i: number) => {
    const p = c.personne;
    const sel = choisi === p.id;
    return (
      <li
        key={p.id} id={`nd-o${p.id}`} role="option" aria-selected={sel} ref={i === actif ? refActif : undefined}
        className={`nd-opt${i === actif ? ' is-actif' : ''}${sel ? ' is-sel' : ''}`} onClick={() => onChoisir(p.id)} onMouseMove={() => { if (actif !== i) onActif(i); }}
      >
        <span className="nd-av" aria-hidden="true">{initiales(p)}</span>
        <span className="nd-opt-txt">
          <span className="nd-opt-nom">
            {p.display_name}
            {p.is_stagiaire && <span className="badge">Stagiaire</span>}
            {p.is_externe && <span className="badge">Externe</span>}
            {sel && <span className="badge badge-ok">choisi·e</span>}
          </span>
          <span className="nd-opt-xp">{texteExperience(c)}</span>
          <span className={`nd-opt-dispo${c.dispo ? '' : ' is-non'}`}>
            {c.dispo ? <Check size={12} aria-hidden="true" /> : <AlertTriangle size={12} aria-hidden="true" />}{c.dispo ? (c.verifiee ? 'disponible' : 'disponibilité non vérifiée') : c.raison}
          </span>
        </span>
      </li>
    );
  };
  let i = 0;

  return (
    <div className="nd-liste">
      <label className="form-label nd-cb-l" htmlFor="nd-cb">{etiquette}</label>
      <input
        id="nd-cb" className="form-input" data-fid="cb" role="combobox" aria-expanded="true" aria-controls="nd-lb" aria-autocomplete="list" autoComplete="off"
        placeholder="Rechercher un nom…" value={filtre} aria-activedescendant={actif >= 0 && liste[actif] ? `nd-o${liste[actif].personne.id}` : undefined}
        onChange={(e) => { onFiltre(e.target.value); onActif(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); onActif(Math.max(0, Math.min(liste.length - 1, actif + (e.key === 'ArrowDown' ? 1 : -1)))); }
          else if (e.key === 'Enter' && liste[actif]) { e.preventDefault(); onChoisir(liste[actif].personne.id); }
        }}
      />
      <ul className="nd-lb" id="nd-lb" role="listbox" aria-label="Remplaçant·e·s possibles, disponibles d'abord">
        {dispo.length > 0 && <li role="presentation" className="nd-grp">{verifiees ? 'Disponibles' : "Pas d'absence connue (les absences n'ont pas pu être chargées)"}</li>}
        {dispo.map((c) => option(c, i++))}
        {non.length > 0 && <li role="presentation" className="nd-grp">Pas disponibles (signalé, pas bloquant)</li>}
        {non.map((c) => option(c, i++))}
        {vis.length === 0 && <li role="presentation" className="nd-grp">Aucun nom ne correspond.</li>}
      </ul>
    </div>
  );
}
